/* __next_internal_client_entry_do_not_use__ Collapsable auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from "classnames";
import * as React from "react";
import styled, { css } from "styled-components";
/**
 * A container with an animation that will show and hide the content contained within it
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/collapsable)
 */ export var Collapsable = function(_param) {
    var isCollapsed = _param.isCollapsed, _param_transitionDuration = _param.transitionDuration, transitionDuration = _param_transitionDuration === void 0 ? 0.25 : _param_transitionDuration, _param_transitionDelay = _param.transitionDelay, transitionDelay = _param_transitionDelay === void 0 ? 0 : _param_transitionDelay, _param_closedHeight = _param.closedHeight, closedHeight = _param_closedHeight === void 0 ? 0 : _param_closedHeight, props = _object_without_properties(_param, [
        "isCollapsed",
        "transitionDuration",
        "transitionDelay",
        "closedHeight"
    ]);
    var ref = React.useRef(null);
    var _React_useState = _sliced_to_array(React.useState(closedHeight), 2), maxHeight = _React_useState[0], setMaxHeight = _React_useState[1];
    var isInitialTransition = useIsInitialValue(isCollapsed);
    var onTransition = React.useCallback(function(event) {
        if (ref.current && event.target === ref.current) {
            if (event.type === "transitionstart") {
                ref.current.classList.add("transitioning");
            } else {
                ref.current.classList.remove("transitioning");
            }
        }
    }, [
        ref
    ]);
    React.useLayoutEffect(function() {
        var element = ref.current;
        if (element) {
            element.addEventListener("transitionend", onTransition);
            element.addEventListener("transitioncancel", onTransition);
            element.addEventListener("transitionstart", onTransition);
            return function() {
                element.removeEventListener("transitionend", onTransition);
                element.removeEventListener("transitioncancel", onTransition);
                element.removeEventListener("transitionstart", onTransition);
            };
        } else {
            return undefined;
        }
    }, [
        onTransition,
        ref
    ]);
    React.useLayoutEffect(function() {
        if (ref.current) {
            var computedStyle = getComputedStyle(ref.current);
            var height = ref.current.scrollHeight;
            var topPadding = parseFloat(computedStyle.paddingTop) || 0;
            var bottomPadding = parseFloat(computedStyle.paddingBottom) || 0;
            setMaxHeight(isCollapsed ? closedHeight : height + topPadding + bottomPadding);
        }
    }, [
        closedHeight,
        isCollapsed
    ]);
    return /*#__PURE__*/ _jsx(CollapsableWrapper, _object_spread_props(_object_spread({}, props), {
        className: classNames(props.className, {
            /* Preemptively add class 'transitioning' to prevent flicker of
             * 'visibility: hidden' between when React finishes rendering,
             * and the 'transitionstart' handler is called.
             */ transitioning: !isInitialTransition
        }),
        $isCollapsed: isCollapsed,
        $transitionDuration: isInitialTransition ? 0 : transitionDuration,
        $transitionDelay: isInitialTransition ? 0 : transitionDelay,
        ref: ref,
        $maxHeight: "".concat(maxHeight, "px")
    }));
};
var CollapsableWrapper = styled.div.withConfig({
    componentId: "sc-36bf5ae3-0"
})([
    "transition:",
    ";overflow:",
    ";visibility:",
    ";max-height:",
    ";&.transitioning{overflow:hidden;visibility:inherit;max-height:",
    ";}"
], function(param) {
    var $transitionDuration = param.$transitionDuration, $transitionDelay = param.$transitionDelay;
    return css([
        "max-height ",
        "s ease ",
        "s"
    ], $transitionDuration, $transitionDelay);
}, function(param) {
    var $isCollapsed = param.$isCollapsed;
    return $isCollapsed ? "hidden" : "visible";
}, function(param) {
    var $isCollapsed = param.$isCollapsed;
    return $isCollapsed ? "hidden" : "inherit";
}, function(param) {
    var $isCollapsed = param.$isCollapsed, $maxHeight = param.$maxHeight;
    return $isCollapsed ? $maxHeight : "unset";
}, function(param) {
    var $maxHeight = param.$maxHeight;
    return $maxHeight;
});
var useIsInitialValue = function(propValue) {
    var _React_useState = _sliced_to_array(React.useState(propValue), 1), initialValue = _React_useState[0];
    var refHasChanged = React.useRef(false);
    if (initialValue !== propValue) {
        refHasChanged.current = true;
    }
    return !refHasChanged.current;
};
